import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import "@qlik/qlik/src/assets/vendor/slick/slick.css";
import "@qlik/qlik/src/assets/vendor/slick/slick-theme.css";
import Media from "@qlik/qlik/src/components/Media";
import Cta from "@qlik/qlik/src/components/Cta";
import { getTransformedId } from "@qlik/qlik/src/lib/util/format";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import { renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";

/**
 * TabSlider component
 * @param {Object} props - The props for the TabSlider component
 * @param {Object} props.data - The data object
 */
const TabSlider = ({ data }) => {
    const { customId, id, title, subtitle, description, backgroundColor, mediaAsset = [], cardItems = [] } = data ?? {};
    // Get the section padding class name
    const sectionPaddingClassName = getSpacingClassName(data?.additionalConfiguration, "md:pt-30 md:pb-25");
    // Check if the first row should be rendered based on the data properties
    const shouldRenderFirstRow = title || subtitle || description?.description;

    // State variables for slider navigation
    const [sliderTabNavigation, setSliderTabNavigation] = useState(null);
    const [sliderContentNavigation, setSliderContentNavigation] = useState(null);
    const [sliderTab, setSliderTab] = useState(null);
    const [sliderContent, setSliderContent] = useState(null);

    // Effect to update slider navigation when sliderTab or sliderContent changes
    useEffect(() => {
        setSliderTabNavigation(sliderTab);
        setSliderContentNavigation(sliderContent);
    }, [sliderTab, sliderContent]);

    // Settings for the slider tab
    const settingsSliderTab = {
        slidesToShow: 5,
        slidesToScroll: 1,
        arrows: false,
        asNavFor: ".slider-for",
        centerMode: false,
        swipeToSlide: true,
        centerPadding: 0,
        infinite: false,
        variableWidth: false,
        focusOnSelect: true,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 3,
                    variableWidth: false,
                },
            },
        ],
    };

    // Settings for the slider content
    const settingsSliderContent = {
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        cssEase: "linear",
        centerMode: true,
        centerPadding: 0,
        asNavFor: ".slider-nav",
        infinite: false,
        fade: true,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    dots: false,
                },
            },
        ],
    };

    // Determine the section background color based on data.backgroundColor
    const { sectionColorMapping } = getColorClassName(backgroundColor);

    // Rendering the TabSlider component
    return (
        <section
            data-section="true"
            id={customId ?? id}
            className={`${sectionPaddingClassName} scroll-mt-45 ${sectionColorMapping.bgColor || "bg-stone-light"}`}
        >
            <div className="container pr-0 sm:pr-unset">
                {shouldRenderFirstRow && (
                    <div className="row mr-0 sm:mr-auto">
                        <div className="col-12 mb-5 md:mb-14.5">
                            {title && (
                                <h2
                                    className={`text-center pr-10 md:pr-0 ${sectionColorMapping.primaryTextColor}`}
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                                />
                            )}
                            {subtitle && (
                                <p className={`body-big text-center mt-5 mb-5 ${sectionColorMapping.primaryTextColor}`}>
                                    {subtitle}
                                </p>
                            )}
                            {description?.description && (
                                <p className={`body-big text-center mb-5 ${sectionColorMapping.primaryTextColor}`}>
                                    {description?.description}
                                </p>
                            )}
                        </div>
                    </div>
                )}
                <div className="row overflow-hidden  mr-0 sm:mr-auto">
                    <div className="col-12 ">
                        <div className={`${styles?.qlikTabSlider} qlikTabSlider`}>
                            <Slider
                                className="mb-6.5 md:mb-7.5   TabSliderContent qlikSlider pr-1.25 sm:pr-0"
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...settingsSliderContent}
                                asNavFor={sliderContentNavigation}
                                ref={(slider) => setSliderTab(slider)}
                            >
                                {cardItems?.map((slide) => (
                                    <div key={slide.id}>
                                        <div
                                            className={` pt-5 pl-5 pr-7.5 pb-5 md:p-10 rounded-sm bg-blue   h-full   md:shadow-lg-none bg-no-repeat bg-right ${
                                                slide?.cta && slide?.title
                                                    ? `${styles.backgroundCTA}`
                                                    : `${styles.background}`
                                            } `}
                                            style={
                                                mediaAsset?.[0]
                                                    ? { backgroundImage: `url(${mediaAsset?.[0]?.secure_url})` }
                                                    : {}
                                            }
                                        >
                                            <div
                                                className={`flex flex-col-reverse md:flex-row justify-between  slideWrapper ${
                                                    !slide?.title ? "no-title" : ""
                                                } `}
                                            >
                                                <div
                                                    className={`flex flex-col slider-text-wrapper h-inherit justify-between gap-3.25 ${
                                                        slide?.cta && slide?.title ? "has-title-with-button" : ""
                                                    }
                                                    ${!slide?.cta && slide?.title ? "md:gap-20" : ""}  `}
                                                >
                                                    <blockquote
                                                        className={`text-md md:text-3xl  relative leading-tight  tracking-tight4  md:tracking-tight7 indent-[6px] md:indent-[8px] -mt-2.25 max-w-[337px]  text-white font-semibold pb-0 ${styles.blockquote}`}
                                                        // eslint-disable-next-line react/no-danger
                                                        dangerouslySetInnerHTML={{
                                                            __html: renderSanitizedHtmlContent(
                                                                slide?.description?.description
                                                            ),
                                                        }}
                                                    />
                                                    <div>
                                                        {(slide?.title || slide?.subtitle) && (
                                                            <div
                                                                className={`${
                                                                    slide?.cta ? "md:mb-7.5" : "md:mb-0"
                                                                } flex flex-col mt-2.5 mb-2.5 md:mt-0`}
                                                            >
                                                                {slide?.title && (
                                                                    <h3 className="text-white text-base md:text-xl font-semibold leading-tight  tracking-normal md:mb-1.5">
                                                                        {slide.title}
                                                                    </h3>
                                                                )}
                                                                {slide?.subtitle && (
                                                                    <span className="text-white  max-w-55.5 md:max-w-max leading-tight md:leading-relaxed text-xs md:text-sm font-normal mt-2 md:mt-auto">
                                                                        {slide.subtitle}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {slide?.cta && (
                                                            <div className="flex gap-3.75 flex-wrap max-w-92.5 mt-5.25 md:max-w-125 lg:max-w-155">
                                                                {slide?.cta?.map((button) => (
                                                                    <Cta key={button.id} data={button} />
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div
                                                    className={`flex justify-center	md:justify-center	 mb-5  md:mb-0 h-[122px]  md:h-auto  items-center ${styles.imageWrapper}`}
                                                >
                                                    {slide?.media?.[0] && (
                                                        <Media
                                                            media={slide?.media?.[0]}
                                                            className={` self-center w-full h-full object-none `}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </Slider>

                            <Slider
                                className={`${cardItems?.length === 3 ? styles.threeCard : styles.defaultCard} ${
                                    cardItems?.length === 5 ? styles.fiveCard : styles.defaultCard
                                } TabSliderTab md:!block  pr-0 `}
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...settingsSliderTab}
                                asNavFor={sliderTabNavigation}
                                ref={(slider) => setSliderContent(slider)}
                            >
                                {cardItems?.map((slide) => (
                                    <div
                                        className="group slideWrapper cursor-pointer relative
                        before:content-empty before:absolute before:w-full before:h-0.5 before:bg-stone/50 before:inset-x-0 before:bottom-0
                        after:content-empty after:absolute after:w-0 after:h-0.5  after:inset-x-0 after:bottom-0
                        md:pl-0 lg:hover:after:w-full max-w-[199px]"
                                        key={slide.id}
                                    >
                                        {slide?.mediaIcon && (
                                            <Media media={slide?.mediaIcon} className="w-full h-full object-none" />
                                        )}
                                    </div>
                                ))}
                            </Slider>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

// Export the TabSlider component
export default TabSlider;
